import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PhygitalIcon from "@/components/PhygitalIcon";
import EarlyAccessModal from "@/components/EarlyAccessModal";
import SharedFooter from "@/components/SharedFooter";
import AllCategoriesSection from "@/components/AllCategoriesSection";
import { EditableText, EditableSection, useAdmin } from "@/components/AdminContentEditor";
import PageMeta from "@/components/PageMeta";
import {
  Sparkles,
  Users,
  Heart,
  ArrowRight,
  Calendar,
  Star,
  CheckCircle2,
  Eye,
  MessageCircle,
  Bell,
  Clock,
  Image,
  Paintbrush,
  Camera,
  UserCheck,
  RefreshCw,
  Send,
  Check,
  X,
  AlertCircle,
  Smile,
  Info,
  Building2,
  Palette,
  Scissors,
  Droplets,
  Timer,
  DollarSign,
  TrendingUp,
  ShoppingBag,
  Wine
} from "lucide-react";

const NailSalesPage = () => {
  const [viewMode, setViewMode] = useState("solo"); // "solo" or "salon"
  const [demoTab, setDemoTab] = useState("client");
  const [showEarlyAccessModal, setShowEarlyAccessModal] = useState(false);
  const { editMode } = useAdmin();

  useEffect(() => {
    document.title = "phygital - For Nail Technicians";
  }, []);

  // Nail services
  const nailServices = [
    { name: "Classic Mani", duration: "30 min", price: "$35+", desc: "Shape, buff & polish" },
    { name: "Gel Manicure", duration: "45 min", price: "$55+", desc: "Long-lasting gel" },
    { name: "Full Set Acrylics", duration: "1.5 hrs", price: "$85+", desc: "Extensions with design" },
    { name: "Nail Art", duration: "30 min+", price: "$15+", desc: "Custom designs" },
  ];

  // Mock client preferences
  const mockClientPrefs = {
    name: "Client",
    defaults: {
      nailShape: "Almond",
      length: "Medium",
      allergies: "None",
      sensitivities: "Cuticles sensitive"
    },
    thisAppt: {
      service: "Gel Manicure",
      color: "Nude with gold accent",
      design: "Minimal French tip",
      inspo: ["Pinterest board shared"],
      notes: "Want to try chrome powder if time allows"
    },
    vibe: "Quiet/relaxing",
    timing: "On time"
  };

  // Benefits for solo nail techs
  const soloBenefits = [
    {
      icon: Image,
      title: "Inspo Before They Arrive",
      desc: "Clients share Pinterest boards, photos, and design ideas when they book. Not when they sit down."
    },
    {
      icon: Clock,
      title: "Accurate Time Estimates",
      desc: "Know if it's a simple color or intricate nail art before scheduling. No more rushed appointments."
    },
    {
      icon: Palette,
      title: "Color & Product Prep",
      desc: "See their preferred shades and know what to pull from your kit before they walk in."
    },
    {
      icon: AlertCircle,
      title: "Allergy & Sensitivity Alerts",
      desc: "Know about sensitivities to acetone, gel allergies, or cuticle sensitivity ahead of time."
    },
    {
      icon: Heart,
      title: "Remember Every Client",
      desc: "Shape preference, length, favorite colors. All saved and ready for repeat visits."
    },
    {
      icon: DollarSign,
      title: "Accurate Pricing Upfront",
      desc: "Quote correctly based on design complexity. No awkward price surprises at checkout."
    }
  ];

  // Benefits for salons
  const salonBenefits = [
    {
      icon: Users,
      title: "Assign the Right Tech",
      desc: "Match nail art requests to your art specialist, simple manis to faster techs."
    },
    {
      icon: Clock,
      title: "Optimize Your Book",
      desc: "See design complexity upfront so you can schedule realistically and reduce wait times."
    },
    {
      icon: Building2,
      title: "Consistent Client Experience",
      desc: "Any tech can see a client's preferences. No info lost when their regular tech is out."
    },
    {
      icon: TrendingUp,
      title: "Upsell Opportunities",
      desc: "See when clients are open to trying new services like chrome, 3D art, or extensions."
    },
    {
      icon: Star,
      title: "Build Loyalty",
      desc: "Remembering preferences makes clients feel valued. They'll keep coming back."
    },
    {
      icon: Bell,
      title: "Reduce No-Shows",
      desc: "Invested clients who share preferences are less likely to cancel last minute."
    }
  ];

  // Pain points
  const painPoints = [
    { problem: "Client shows inspo at the chair", solution: "See their vision before they arrive" },
    { problem: "Rushed because design takes longer", solution: "Know complexity when booking" },
    { problem: "Wrong color/shade assumptions", solution: "Client confirms exact preferences" },
    { problem: "Forgetting regular client details", solution: "Preferences saved & synced" },
  ];

  return (
    <div className="min-h-screen bg-white">
      <PageMeta 
        title="Phygital for Nail Pros - See Their Vision Before They Sit Down"
        description="Collect design inspo, nail shape preferences, and prep details before clients arrive. Built for nail salons and independent techs."
      />
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2">
            <PhygitalIcon className="w-8 h-8" />
            <span className="font-semibold text-xl">phygital</span>
          </Link>
          <div className="flex items-center gap-4">
            <Badge variant="secondary" className="hidden sm:flex gap-1 bg-pink-100 text-pink-700">
              <Eye className="w-3 h-3" />
              Demo Mode
            </Badge>
            <Button 
              size="sm" 
              className="rounded-full"
              onClick={() => setShowEarlyAccessModal(true)}
              data-testid="nail-early-access-btn"
            >
              Get Early Access
              <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <EditableSection sectionId="nail-hero" label="Nail Hero" className={editMode ? "pt-24 sm:pt-28 pb-12 sm:pb-16 px-4 relative overflow-hidden" : "pt-24 sm:pt-28 pb-12 sm:pb-16 px-4 relative overflow-hidden"}>
        <div className="absolute inset-0 bg-gradient-to-br from-pink-50 via-white to-fuchsia-50/30" />
        <div className="absolute top-20 right-0 w-96 h-96 bg-gradient-to-br from-pink-200/20 to-fuchsia-200/20 rounded-full blur-3xl" />
        
        <div className="relative max-w-6xl mx-auto text-center">
          <Badge className="mb-4 sm:mb-6 bg-gradient-to-r from-pink-500 to-fuchsia-500 text-white border-0">
            <Paintbrush className="w-3 h-3 mr-1" />
            <EditableText sectionId="nail-hero" field="badge" defaultValue="Nail Tech Pilot by Phygital" className="inline" />
          </Badge>
          
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
            <EditableText sectionId="nail-hero" field="title_line1" defaultValue="See Their Vision" className="inline" /><br />
            <span className="bg-gradient-to-r from-pink-500 via-fuchsia-500 to-purple-500 bg-clip-text text-transparent">
              <EditableText sectionId="nail-hero" field="title_line2" defaultValue="Before They Sit Down" className="inline" />
            </span>
          </h1>
          
          <p className="text-sm sm:text-lg text-gray-700 max-w-2xl mx-auto mb-6 sm:mb-10 px-4">
            <EditableText 
              sectionId="nail-hero" 
              field="subtitle" 
              defaultValue="Clients share their inspo, shape, and design preferences when they book. Not when they're already in your chair."
              multiline
            />
          </p>
          
          {/* Solo/Salon Toggle */}
          <div className="flex justify-center mb-6 sm:mb-8">
            <div className="inline-flex bg-gray-100 rounded-full p-1">
              <button
                onClick={() => setViewMode("solo")}
                className={`px-4 sm:px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  viewMode === "solo" 
                    ? "bg-white shadow text-gray-900" 
                    : "text-gray-700 hover:text-gray-900"
                }`}
              >
                Solo Nail Techs
              </button>
              <button
                onClick={() => setViewMode("salon")}
                className={`px-4 sm:px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  viewMode === "salon" 
                    ? "bg-white shadow text-gray-900" 
                    : "text-gray-700 hover:text-gray-900"
                }`}
              >
                Salons & Studios
              </button>
            </div>
          </div>
          
          <div className="flex justify-center mb-8 sm:mb-12">
            <Button 
              size="lg" 
              className="rounded-full bg-gray-900 hover:bg-gray-800 text-sm sm:text-base px-6 sm:px-8"
              onClick={() => document.getElementById('benefits').scrollIntoView({ behavior: 'smooth' })}
            >
              See How It Works
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {/* Value Props */}
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap justify-center gap-2 sm:gap-6 text-[11px] sm:text-sm max-w-[320px] sm:max-w-none mx-auto">
            {[
              { icon: Calendar, text: "Works with any booker" },
              { icon: Image, text: "Inspo before booking" },
              { icon: Palette, text: "Color preferences" },
              { icon: CheckCircle2, text: "Design approval flow" }
            ].map((item, i) => (
              <div key={i} className="flex items-center justify-center sm:justify-start gap-1.5 sm:gap-2 text-gray-700 whitespace-nowrap">
                <item.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-pink-500 flex-shrink-0" />
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </EditableSection>

      {/* Client Flow Preview */}
      <section className="py-12 px-4 bg-gradient-to-br from-pink-50 via-white to-fuchsia-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <Badge className="mb-4 bg-gradient-to-r from-pink-500 to-fuchsia-500 text-white border-0">
              <Sparkles className="w-3 h-3 mr-1" />
              What You See
            </Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              Design, Inspo, and Prep Details
            </h2>
            <p className="text-gray-700 max-w-xl mx-auto">
              Each client shares their vision before arriving.
              <br className="hidden sm:block" />
              <span className="sm:block">You see the supplies and time needed.</span>
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Phone Mockup - Client View */}
            <div className="flex justify-center">
              <div className="relative">
                <div className="w-[280px] bg-gray-900 rounded-[2.5rem] p-3 shadow-2xl">
                  <div className="bg-white rounded-[2rem] overflow-hidden">
                    <div className="bg-gray-100 px-4 py-2 flex justify-between items-center text-xs text-gray-700">
                      <span>9:41</span>
                      <span>Client View</span>
                    </div>
                    <div className="p-4 space-y-3">
                      <div className="text-center">
                        <p className="text-xs text-gray-700">Share your nail vision</p>
                        <p className="font-semibold text-sm">for your appointment</p>
                      </div>
                      
                      {/* Shape Selection with Vector Icons */}
                      <div className="bg-gray-50 rounded-lg p-2">
                        <p className="text-[10px] text-gray-700 mb-2">Nail Shape</p>
                        <div className="flex justify-between gap-1">
                          {[
                            { name: "Square", icon: (
                              <svg viewBox="0 0 24 32" className="w-4 h-5">
                                <path d="M4 28 L4 8 L20 8 L20 28 Z" fill="currentColor" stroke="currentColor" strokeWidth="1"/>
                              </svg>
                            )},
                            { name: "Round", icon: (
                              <svg viewBox="0 0 24 32" className="w-4 h-5">
                                <path d="M4 28 L4 12 Q4 4 12 4 Q20 4 20 12 L20 28 Z" fill="currentColor" stroke="currentColor" strokeWidth="1"/>
                              </svg>
                            )},
                            { name: "Almond", icon: (
                              <svg viewBox="0 0 24 32" className="w-4 h-5">
                                <path d="M4 28 L4 16 Q4 4 12 2 Q20 4 20 16 L20 28 Z" fill="currentColor" stroke="currentColor" strokeWidth="1"/>
                              </svg>
                            )},
                            { name: "Coffin", icon: (
                              <svg viewBox="0 0 24 32" className="w-4 h-5">
                                <path d="M4 28 L4 14 L8 4 L16 4 L20 14 L20 28 Z" fill="currentColor" stroke="currentColor" strokeWidth="1"/>
                              </svg>
                            )},
                            { name: "Stiletto", icon: (
                              <svg viewBox="0 0 24 32" className="w-4 h-5">
                                <path d="M4 28 L4 18 L12 2 L20 18 L20 28 Z" fill="currentColor" stroke="currentColor" strokeWidth="1"/>
                              </svg>
                            )}
                          ].map((shape, i) => (
                            <div key={i} className={`flex-1 flex flex-col items-center py-1.5 rounded ${i === 2 ? 'bg-pink-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
                              {shape.icon}
                              <span className="text-[8px] mt-0.5">{shape.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Length Selection with Vector Icons */}
                      <div className="bg-gray-50 rounded-lg p-2">
                        <p className="text-[10px] text-gray-700 mb-2">Length</p>
                        <div className="flex gap-1">
                          {[
                            { name: "Short", icon: (
                              <svg viewBox="0 0 32 24" className="w-6 h-4">
                                <path d="M4 20 L4 8 Q4 4 8 4 L12 4 Q16 4 16 8 L16 20 Z" fill="currentColor"/>
                                <rect x="4" y="18" width="12" height="6" fill="#FFE4E1" stroke="currentColor" strokeWidth="0.5"/>
                              </svg>
                            )},
                            { name: "Medium", icon: (
                              <svg viewBox="0 0 32 24" className="w-6 h-4">
                                <path d="M4 20 L4 6 Q4 2 10 2 L14 2 Q20 2 20 6 L20 20 Z" fill="currentColor"/>
                                <rect x="4" y="18" width="16" height="6" fill="#FFE4E1" stroke="currentColor" strokeWidth="0.5"/>
                              </svg>
                            )},
                            { name: "Long", icon: (
                              <svg viewBox="0 0 32 24" className="w-6 h-4">
                                <path d="M4 20 L4 4 Q4 0 12 0 L16 0 Q24 0 24 4 L24 20 Z" fill="currentColor"/>
                                <rect x="4" y="18" width="20" height="6" fill="#FFE4E1" stroke="currentColor" strokeWidth="0.5"/>
                              </svg>
                            )},
                            { name: "XL", icon: (
                              <svg viewBox="0 0 32 24" className="w-6 h-4">
                                <path d="M2 20 L2 2 Q2 -2 14 -2 L18 -2 Q30 -2 30 2 L30 20 Z" fill="currentColor"/>
                                <rect x="2" y="18" width="28" height="6" fill="#FFE4E1" stroke="currentColor" strokeWidth="0.5"/>
                              </svg>
                            )}
                          ].map((len, i) => (
                            <div key={i} className={`flex-1 flex flex-col items-center py-1.5 rounded ${i === 1 ? 'bg-pink-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
                              {len.icon}
                              <span className="text-[8px] mt-0.5">{len.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Finish Selection */}
                      <div className="bg-gray-50 rounded-lg p-2">
                        <p className="text-[10px] text-gray-700 mb-2">Finish</p>
                        <div className="flex flex-wrap gap-1">
                          {["Glossy", "Matte", "Chrome", "Glitter", "French"].map((finish, i) => (
                            <span key={i} className={`px-2 py-1 rounded text-[9px] ${i === 0 ? 'bg-pink-500 text-white' : 'bg-gray-200 text-gray-700'}`}>
                              {finish}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      {/* Inspo Upload */}
                      <div className="p-2 bg-gray-50 rounded-lg">
                        <p className="text-[10px] text-gray-700 mb-1">Add inspiration</p>
                        <div className="flex gap-1">
                          <div className="w-10 h-10 rounded bg-pink-100 flex items-center justify-center">
                            <Image className="w-4 h-4 text-pink-400" />
                          </div>
                          <div className="w-10 h-10 rounded bg-pink-100 flex items-center justify-center">
                            <Image className="w-4 h-4 text-pink-400" />
                          </div>
                          <div className="w-10 h-10 rounded border-2 border-dashed border-gray-300 flex items-center justify-center">
                            <Camera className="w-4 h-4 text-gray-600" />
                          </div>
                        </div>
                      </div>
                      
                      <Badge className="bg-pink-100 text-pink-700 text-[10px] w-full justify-center">
                        Preferences saved ✓
                      </Badge>
                    </div>
                  </div>
                </div>
                <p className="text-center text-sm text-gray-700 mt-3">Client shares their vision</p>
              </div>
            </div>

            {/* Laptop Mockup - Nail Tech View */}
            <div className="flex justify-center">
              <div className="relative">
                <div className="w-[400px] bg-gray-800 rounded-t-xl p-2">
                  <div className="bg-white rounded-lg overflow-hidden">
                    <div className="bg-gray-100 px-3 py-1.5 flex items-center gap-2 border-b">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 rounded-full bg-red-400" />
                        <div className="w-2 h-2 rounded-full bg-yellow-400" />
                        <div className="w-2 h-2 rounded-full bg-green-400" />
                      </div>
                      <div className="flex-1 bg-white rounded px-2 py-0.5 text-[10px] text-gray-700">phygital.systems/nail/dashboard</div>
                    </div>
                    <div className="p-3">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-semibold text-sm">Today's Appointments</span>
                        <Badge className="bg-green-100 text-green-700 text-[10px]">4 Ready</Badge>
                      </div>
                      
                      {/* Appointment Card */}
                      <div className="border border-pink-200 rounded-lg p-2 mb-2 bg-pink-50/50">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-8 h-8 rounded-full bg-pink-200 flex items-center justify-center text-pink-700 text-xs font-bold">M</div>
                          <div className="flex-1">
                            <p className="text-xs font-medium">Maya - 10:00 AM</p>
                            <p className="text-[10px] text-gray-700">Gel Mani - Almond, Medium</p>
                          </div>
                          {/* Shape & Length Icons */}
                          <div className="flex gap-1">
                            <div className="w-6 h-6 rounded bg-pink-100 flex items-center justify-center text-pink-600" title="Almond shape">
                              <svg viewBox="0 0 24 32" className="w-3 h-4">
                                <path d="M4 28 L4 16 Q4 4 12 2 Q20 4 20 16 L20 28 Z" fill="currentColor"/>
                              </svg>
                            </div>
                            <div className="w-6 h-6 rounded bg-pink-100 flex items-center justify-center text-pink-600" title="Medium length">
                              <svg viewBox="0 0 32 24" className="w-4 h-3">
                                <path d="M4 20 L4 6 Q4 2 10 2 L14 2 Q20 2 20 6 L20 20 Z" fill="currentColor"/>
                              </svg>
                            </div>
                          </div>
                        </div>
                        
                        {/* Design Details */}
                        <div className="bg-white rounded p-2 mb-2">
                          <p className="text-[10px] font-medium text-gray-700 mb-1">Design Request:</p>
                          <p className="text-[10px] text-gray-700 mb-2">Nude base with gold chrome French tips</p>
                          <div className="flex gap-1">
                            <div className="w-8 h-8 rounded bg-pink-100 flex items-center justify-center">
                              <Image className="w-3 h-3 text-pink-400" />
                            </div>
                            <div className="w-8 h-8 rounded bg-pink-100 flex items-center justify-center">
                              <Image className="w-3 h-3 text-pink-400" />
                            </div>
                          </div>
                        </div>
                        
                        {/* Supplies */}
                        <div className="flex flex-wrap gap-1">
                          {["Nude gel", "Gold chrome", "Top coat"].map((s, i) => (
                            <span key={i} className="px-2 py-0.5 bg-pink-100 text-pink-700 rounded text-[9px]">{s}</span>
                          ))}
                        </div>
                      </div>
                      
                      {/* Alert */}
                      <div className="bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-2">
                        <AlertCircle className="w-3 h-3 text-amber-600 mt-0.5" />
                        <p className="text-[10px] text-amber-800">Client noted: Sensitive to acetone remover</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="w-[420px] h-3 bg-gray-700 rounded-b-xl mx-auto" />
                <p className="text-center text-sm text-gray-700 mt-3">You see design + supplies needed</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-12 sm:py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-xl sm:text-3xl font-bold text-gray-900 mb-3">
              {viewMode === "solo" ? "Built for Independent Nail Techs" : "Built for Salons & Studios"}
            </h2>
            <p className="text-gray-700 max-w-xl mx-auto text-sm sm:text-base px-4">
              {viewMode === "solo" 
                ? "Mobile, booth rent, or studio. Phygital helps you prep better and serve faster."
                : "Give your whole team visibility into client preferences for smoother operations."
              }
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {(viewMode === "solo" ? soloBenefits : salonBenefits).map((benefit, i) => (
              <Card key={i} className="border-gray-200 hover:border-pink-300 hover:shadow-lg transition-all">
                <CardContent className="p-4 sm:p-6">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-pink-100 to-fuchsia-100 flex items-center justify-center mb-3 sm:mb-4">
                    <benefit.icon className="w-5 h-5 sm:w-6 sm:h-6 text-pink-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">{benefit.title}</h3>
                  <p className="text-xs sm:text-sm text-gray-700">{benefit.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="py-12 sm:py-16 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-xl sm:text-3xl font-bold text-gray-900 mb-3">
              Sound Familiar?
            </h2>
            <p className="text-gray-700 text-sm sm:text-base">Common frustrations we solve</p>
          </div>

          <div className="space-y-3 sm:space-y-4">
            {painPoints.map((point, i) => (
              <div key={i} className="bg-white rounded-xl p-4 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                <div className="flex items-center gap-2 sm:gap-3 flex-1">
                  <X className="w-4 h-4 sm:w-5 sm:h-5 text-red-500 flex-shrink-0" />
                  <p className="text-gray-700 text-sm sm:text-base">{point.problem}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-600 hidden sm:block" />
                <div className="flex items-center gap-2 sm:gap-3 flex-1">
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0" />
                  <p className="text-gray-700 font-medium text-sm sm:text-base">{point.solution}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-12 sm:py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-xl sm:text-3xl font-bold text-gray-900 mb-3">
              Works With All Your Services
            </h2>
            <p className="text-gray-700 text-sm sm:text-base">Clients share preferences for any service</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            {nailServices.map((service, i) => (
              <div key={i} className="bg-gradient-to-br from-pink-50 to-fuchsia-50 rounded-xl p-3 sm:p-4 text-center hover:shadow-md transition-all">
                <Paintbrush className="w-5 h-5 sm:w-6 sm:h-6 text-pink-500 mx-auto mb-2" />
                <p className="font-semibold text-gray-900 text-xs sm:text-sm">{service.name}</p>
                <p className="text-[10px] sm:text-xs text-gray-700">{service.duration}</p>
                <p className="text-[10px] sm:text-xs font-medium text-pink-600 mt-1">{service.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 px-4 bg-gradient-to-br from-pink-500 via-fuchsia-500 to-purple-500">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-xl sm:text-3xl font-bold text-white mb-4">
            Ready to See Client Visions Before They Arrive?
          </h2>
          <p className="text-pink-100 mb-6 sm:mb-8 text-sm sm:text-base px-4">
            Join the pilot program and get early access. Works with your existing booking system.
          </p>
          <Button 
            size="lg" 
            className="rounded-full bg-white text-pink-600 hover:bg-pink-50 text-sm sm:text-base px-6 sm:px-8"
            onClick={() => setShowEarlyAccessModal(true)}
            data-testid="nail-cta-early-access-btn"
          >
            Get Early Access
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <p className="text-pink-200 text-xs mt-4">Free during pilot • No booking system changes required</p>
        </div>
      </section>

      {/* Built for Every Service Industry - Hidden for now */}
      {/* <AllCategoriesSection currentCategory="nail" /> */}

      {/* Footer */}
      <SharedFooter variant="dark" />
      
      {/* Early Access Modal */}
      <EarlyAccessModal open={showEarlyAccessModal} onOpenChange={setShowEarlyAccessModal} category="nails" />
    </div>
  );
};

export default NailSalesPage;
